import { useFormatting } from "@msys/ui";
import { Functions as FunctionsIcon } from "@mui/icons-material";
import { IconButton, Stack, TextField } from "@mui/material";
import type {
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid-premium";
import React from "react";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { itemHasEstimatedCalculationBox } from "../../doc-items/app-conditions/boxes.js";
import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import { AttributeExpressionModal } from "../../doc-items/modals/AttributeExpressionModal.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import { DataGridFloatWithExpressionDisplay } from "../cells/DataGridFloatWithExpressionEditCell.js";
import { makeCalculationValueSetter } from "../cells/helpers.js";
import type { ModifyItemCalculationFn } from "../useModifyItemCalculation.js";
import type { DocDataGridRow } from "../types.js";

interface Options<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
> {
  headerName: string;
  projectId: string | null;
  docId: string;
  canEdit: boolean;
  modifyItemCalculation: ModifyItemCalculationFn;
  itemCanBeEdited?: (item: T) => boolean;
}

function QuantityDisplayCell<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>({
  params,
}: {
  params: GridRenderCellParams<
    DocDataGridRow<T>,
    any,
    any,
    GridTreeNodeWithRender
  >;
}) {
  const { getFormattedFloat } = useFormatting();
  const formattedValue = getFormattedFloat(params.value ?? 0);
  return (
    <DataGridFloatWithExpressionDisplay<T>
      params={params}
      fieldName="estimatedQuantity"
      formattedValue={formattedValue}
    />
  );
}

/**
 * Edit cell for the literal-value case (no expression yet) — float input
 * paired with a formula icon button that opens an empty modal so the user
 * can convert the literal into a formula while editing.
 *
 * The input is implemented inline (rather than via `DataGridFloatEditCell`)
 * because its `onBlur` must skip the commit when focus moves to the formula
 * button — otherwise the modal-open would blur the input, the input would
 * commit + `stopCellEditMode`, and the cell (with the modal mounted inside
 * it) would unmount the moment it appeared. Same pattern as
 * `FormattedFloatWithExpressionField`.
 *
 * Modal close exits edit mode with `ignoreModifications: true` — the modal
 * owns its own mutation, so any save already landed before close.
 */
function QuantityEditCellLiteral({
  params,
  projectId,
  docId,
  itemId,
}: {
  params: GridRenderEditCellParams;
  projectId: string | null;
  docId: string;
  itemId: string;
}) {
  const { id, field, value, api } = params;
  const { getEditableFloat, getFloat } = useFormatting();
  const expressionButtonRef = React.useRef<HTMLButtonElement>(null);
  const [inputValue, setInputValue] = React.useState<string>(() =>
    getEditableFloat(value ?? 0)
  );
  const [isModalOpen, setModalOpen] = React.useState(false);

  const commit = (raw: string) => {
    let parsed = getFloat(raw, false);
    if (Number.isNaN(parsed)) parsed = 0;
    if (parsed < 0) parsed = 0;
    api.setEditCellValue({ id, field, value: parsed });
    api.stopCellEditMode({ id, field });
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ alignItems: "center", height: "100%", width: "100%" }}
    >
      <TextField
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onBlur={e => {
          // Skip commit when focus is intentionally moving to the formula
          // icon — otherwise opening the modal would race against the
          // commit + stopCellEditMode and the modal would unmount instantly.
          if (e.relatedTarget === expressionButtonRef.current) return;
          commit(e.target.value);
        }}
        onKeyDown={e => {
          if (e.key === "Enter") commit((e.target as HTMLInputElement).value);
          else if (e.key === "Escape")
            api.stopCellEditMode({ id, field, ignoreModifications: true });
        }}
        autoFocus
        size="extra-small"
        fullWidth
        variant="outlined"
        sx={{ "& fieldset": { border: "none" } }}
      />
      <RestrictedByCapabilityWithDebug capability="EXPRESSIONS">
        <IconButton
          ref={expressionButtonRef}
          size="small"
          color="secondary"
          onClick={e => {
            e.stopPropagation();
            setModalOpen(true);
          }}
        >
          <FunctionsIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </RestrictedByCapabilityWithDebug>
      {isModalOpen && (
        <AttributeExpressionModal
          projectId={projectId}
          docId={docId}
          itemId={itemId}
          fieldName="estimatedQuantity"
          expression={undefined}
          handleClose={() => {
            setModalOpen(false);
            api.stopCellEditMode({ id, field, ignoreModifications: true });
          }}
        />
      )}
    </Stack>
  );
}

/**
 * Edit cell for the formula case — mounts `AttributeExpressionModal` directly
 * with the current expression seeded. Mirrors the preview's
 * `QuantityInlineEditor` expression-only path (typing a literal here would
 * silently overwrite the formula, so the literal input is bypassed). Modal
 * close exits the cell's edit mode without saving — the modal owns its own
 * mutation, so any save already landed before close.
 */
function QuantityEditCellExpression({
  params,
  projectId,
  docId,
  itemId,
  expression,
}: {
  params: GridRenderEditCellParams;
  projectId: string | null;
  docId: string;
  itemId: string;
  expression: string;
}) {
  const { id, field, api } = params;
  return (
    <RestrictedByCapabilityWithDebug
      capability="EXPRESSIONS"
      otherwise={<ExitCellEditMode params={params} />}
    >
      <AttributeExpressionModal
        projectId={projectId}
        docId={docId}
        itemId={itemId}
        fieldName="estimatedQuantity"
        expression={expression}
        handleClose={() =>
          api.stopCellEditMode({ id, field, ignoreModifications: true })
        }
      />
    </RestrictedByCapabilityWithDebug>
  );
}

/**
 * Exits the cell's edit mode without saving, reverting to the read-only
 * display cell. Used when a capability-less org enters edit mode on a
 * formula cell — the formula editor must not open.
 */
function ExitCellEditMode({ params }: { params: GridRenderEditCellParams }) {
  const { id, field, api } = params;
  React.useEffect(() => {
    api.stopCellEditMode({ id, field, ignoreModifications: true });
  }, [api, id, field]);
  return null;
}

export function createEstimatedQuantityColumn<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>(options: Options<T>): GridColDef<DocDataGridRow<T>> {
  return {
    field: "estimatedQuantity",
    headerName: options.headerName,
    width: 80,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) => row.item?.proposedCalculation?.quantity ?? 0,
    // estimatedQuantity (mutation) maps to quantity (proposedCalculation)
    valueSetter: makeCalculationValueSetter<T>()("quantity", (itemId, value) =>
      options.modifyItemCalculation(itemId, {
        estimatedQuantity: value,
      })
    ),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.isRootItem) return null;
      if (!itemHasEstimatedCalculationBox(item)) return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <QuantityDisplayCell params={params} />;
    },
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.isRootItem) return null;
      if (!itemHasEstimatedCalculationBox(item)) return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      // When the cell carries a formula, open the formula modal directly
      // — mirrors `FormattedFloatWithExpressionField` / the preview's
      // `QuantityInlineEditor`. Typing a literal here would silently
      // overwrite the expression.
      const expression = item.attributeExpressions?.find(
        e => e.attribute === "estimatedQuantity"
      );
      if (expression) {
        return (
          <QuantityEditCellExpression
            params={params}
            projectId={options.projectId}
            docId={options.docId}
            itemId={item.id}
            expression={expression.expr}
          />
        );
      }
      return (
        <QuantityEditCellLiteral
          params={params}
          projectId={options.projectId}
          docId={options.docId}
          itemId={item.id}
        />
      );
    },
  };
}
